import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { LoaderModule } from '@mds/ui';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { GlobalDataService } from '@services/global-data.service';
import { ToastrService } from '@services/toaster.service';
import { CookiesService } from '@services/cookies.service';
import { FAQService } from '@services/FAQ.service';
import { InlineManualService } from '@services/inline-manual.service';
import { LocalStorageService } from '@services/local-storage.service';
import { MakersiteService } from '@services/makersite.service';
import { Subscription, timer } from 'rxjs';
import { MenuService } from '@services/menu.service';
import { NewObjectService } from '@services/new-object.service';
import { HeaderProfileComponent } from './header-profile/header-profile.component';
import { HeaderSearchComponent } from './header-search/header-search.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    HeaderSearchComponent,
    HeaderProfileComponent,
    MdsIconsModule,
    RouterLink,
    LoaderModule,
    MatMenuModule,
    MatTooltipModule,
    MatButtonModule
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit {
  icons = MdsIcon;
  private menuService = inject(MenuService);
  private globalDataService = inject(GlobalDataService);
  private newObjectService = inject(NewObjectService);
  private inlineManualService = inject(InlineManualService);
  private featureFlagsService = inject(FeatureFlagsService);
  private faqService = inject(FAQService);
  focusOutGlobalSearch = false;

  isReloading$ = this.menuService.reloadInProgress$;
  allFeaturs: any[] = [];
  preventSingleClick = false;
  timer: Subscription;
  hideTeamSwitch = true;
  teamList: any[] = [];

  constructor(
    private makersiteService: MakersiteService,
    private toastrService: ToastrService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private cookiesService: CookiesService
  ) {}

  ngOnInit(): void {
    this.allFeaturs = this.globalDataService.getFeatureArray();

    if (this.allFeaturs && this.allFeaturs.includes('inline_manual')) {
      this.inlineManualConfig();
    }

    this.inlineManualInit();

    const teamListInfo = this.localStorageService.getItem('teamListInfo');
    const userInfo = this.localStorageService.getItem('user_info');
    this.teamList = teamListInfo.teams;
    this.hideTeamSwitch = userInfo.features.some((feat) => feat.feature === 'ui_hide_team_switch');
  }

  reloadPage() {
    this.preventSingleClick = false;

    this.timer = timer(400).subscribe(() => {
      if (!this.preventSingleClick) {
        this.menuService.setReloadState(true);
      }
    });
  }

  clearCache() {
    this.preventSingleClick = true;

    this.timer.unsubscribe();

    if (this.router.url.match(/composer|mattermaps|report|[`/`]+explorer|ecodesign|cbam-dashboard/)) {
      const ridList = [];

      ridList.push(
        this.globalDataService.getPpmId()
          ? this.globalDataService.getPpmId().replace('#', '')
          : this.router.url.split('/').pop()
      );

      if (ridList.length) {
        this.makersiteService.clearCacheForComposerAndMattermap(ridList).subscribe(() => {
          this.toastrService.showMessage('Caches will also be cleared!', 1000);
          this.menuService.setReloadState(true);
        });
      }
    } else {
      this.menuService.setReloadState(true);
    }
  }

  inlineManualConfig() {
    this.inlineManualService.openInlineconfig();
  }

  inlineManualInit() {
    this.inlineManualService.openInlineManualInit();
  }

  showInlineManual() {
    if (inline_manual_player && inline_manual_player.showPanel) {
      inline_manual_player.showPanel();
    }
  }

  isTutorialEnabled() {
    return this.featureFlagsService.isTutorialEnabled && this.allFeaturs.includes('inline_manual');
  }

  privacyPolicy() {
    const url = 'https://support.makersite.io/general-faq/makersite-privacy-policy';
    this.faqService.openKnowledgeBaseURL(url);
  }

  openFAQ() {
    this.faqService.openKnowledgeBaseURL();
  }

  focusOffGlobalSearch() {
    this.focusOutGlobalSearch = !this.focusOutGlobalSearch;
  }

  onCreateNewItem() {
    this.newObjectService.createNewObjectDialog();
  }

  switchTeam(team): void {
    this.localStorageService.clear();
    this.cookiesService.clearCookies();
    window.location.href = `https://${team.name}.makersite.app/login`;
  }
}
